import { useState } from 'react';
import type { CalculationResult, SensitivityRow, TemperatureScenarioRow } from '../types';
import { AssumptionTag } from './Primitives';
import { buildSummaryText } from '../utils/summary';
import { formatDate, formatExact, formatNumber } from '../utils/format';
import { APP_META } from '../data/provenance';
import { CONSIDERATION_CHECKLIST } from '../data/engineeringConsiderations';
import { trackEvent } from '../utils/analytics';

interface Props {
  result: CalculationResult | null;
  sensitivity: SensitivityRow[];
  temperatureScenarios: TemperatureScenarioRow[];
  checked: string[];
  verificationHeadline: string;
  developmentPassed: number;
  developmentTotal: number;
}

export function SummaryPanel(props: Props) {
  const { result, sensitivity, temperatureScenarios, checked } = props;
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  if (!result) {
    return <p className="empty-state">The summary is generated from a completed calculation.</p>;
  }

  const summaryText = buildSummaryText({
    result,
    sensitivity,
    temperatureScenarios,
    consideredIds: checked,
    totalConsiderations: CONSIDERATION_CHECKLIST.length,
    verificationHeadline: props.verificationHeadline,
    developmentPassed: props.developmentPassed,
    developmentTotal: props.developmentTotal,
  });

  const copy = async () => {
    trackEvent('summary_copied');
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2500);
    } catch {
      setCopyState('failed');
    }
  };

  const i = result.inputs;

  return (
    <div className="summary">
      <div className="summary-card">
        <div className="summary-card-head">
          <h3 className="panel-title">Calculation &amp; Assumption Summary</h3>
          <span className="summary-version">
            {APP_META.version} · verification {APP_META.calculatorVerificationVersion}
          </span>
        </div>

        <dl className="summary-grid">
          <div>
            <dt>Desired shelf life</dt>
            <dd>
              {i.realTimeDurationValue} {i.realTimeDurationUnit}
              <span className="summary-sub">{formatNumber(result.realTimeDays, 4)} days</span>
            </dd>
          </div>
          <div>
            <dt>Reference temperature</dt>
            <dd>{i.referenceTempC} °C</dd>
          </div>
          <div>
            <dt>Accelerated temperature</dt>
            <dd>{i.acceleratedTempC} °C</dd>
          </div>
          <div>
            <dt>Q10</dt>
            <dd>
              <AssumptionTag>{i.q10}</AssumptionTag>
            </dd>
          </div>
          <div>
            <dt>Calculated AAF</dt>
            <dd>
              {formatNumber(result.aaf, 4)}
              <span className="summary-sub">exact {formatExact(result.aaf)}</span>
            </dd>
          </div>
          <div>
            <dt>Calculated AAT</dt>
            <dd>
              {formatNumber(result.aatDays, 3)} days
              <span className="summary-sub">exact {formatExact(result.aatDays)} days</span>
            </dd>
          </div>
          <div>
            <dt>Start date</dt>
            <dd>{i.startDate ? formatDate(i.startDate) : 'not entered'}</dd>
          </div>
          <div>
            <dt>Projected completion</dt>
            <dd>{result.projectedCompletionDate ? formatDate(result.projectedCompletionDate) : '—'}</dd>
          </div>
          <div>
            <dt>Considerations reviewed</dt>
            <dd>
              {checked.length} / {CONSIDERATION_CHECKLIST.length}
            </dd>
          </div>
          <div>
            <dt>Verification status</dt>
            <dd className="summary-warn">{props.verificationHeadline}</dd>
          </div>
        </dl>

        <p className="summary-boundary">
          This summary states what you entered and what the arithmetic returned. It does not state that any of it is
          appropriate for your product or packaging system.
        </p>
      </div>

      <div className="summary-actions">
        <button type="button" className="primary-button" onClick={copy}>
          {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Copy failed — select the text below' : 'Copy summary'}
        </button>
        <button
          type="button"
          className="secondary-button"
          onClick={() => {
            trackEvent('summary_printed');
            window.print();
          }}
        >
          Print / Save as PDF
        </button>
      </div>

      <details className="disclosure" onToggle={() => trackEvent('summary_generated')}>
        <summary>View the full text output</summary>
        <div className="disclosure-body">
          <pre className="summary-text">{summaryText}</pre>
        </div>
      </details>
    </div>
  );
}
